import React from 'react';
import { TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import Clipboard from '@react-native-clipboard/clipboard';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { Copy01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { ExternalLinkButton } from '@/components/ui/ExternalLinkButton';
import { useCommunity } from '@/hooks/useCommunity';
import { useToastMessages } from '@/hooks/useToastMessages';

interface ICCBillPaymentsProps {
  /** The member's most recent CCBill subscription ID, shown for copying when there is one */
  lastSubscriptionId?: string | null;
}

/** CCBill payment information with the manage-subscription link (no card chrome). */
export function CCBillPayments({ lastSubscriptionId }: ICCBillPaymentsProps) {
  const { t } = useTranslation();
  const community = useCommunity();
  const { showSuccessToast } = useToastMessages();

  const handleCopySubscriptionId = (id: string) => {
    Clipboard.setString(id);
    showSuccessToast(t('account.plans.subscriptionIdCopied'), undefined, 'bottom');
  };

  return (
    <VStack space="md">
      <Text size="sm" className="leading-5 text-typography-600">
        {t('account.plans.ccbillDescription', { communityName: community?.name })}
      </Text>

      <ExternalLinkButton
        url={
          lastSubscriptionId
            ? `https://support.ccbill.com/?subscriptionid=${lastSubscriptionId}`
            : null
        }
        label={t('account.plans.manageCCBill')}
      />

      {lastSubscriptionId && (
        <HStack space="sm" className="flex-wrap items-center">
          <Text size="xs" className="text-typography-500">
            {t('account.plans.subscriptionIdLabel')}:
          </Text>
          <TouchableOpacity
            onPress={() => handleCopySubscriptionId(lastSubscriptionId)}
            activeOpacity={0.7}>
            <Badge
              action="muted"
              variant="solid"
              size="sm"
              className="flex-row items-center gap-1.5">
              <HugeIconsWrapper icon={Copy01Icon} size={16} className="text-typography-800" />
              <BadgeText size="md" className="font-mono">
                {lastSubscriptionId}
              </BadgeText>
            </Badge>
          </TouchableOpacity>
        </HStack>
      )}
    </VStack>
  );
}
